import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MainContainer } from '../MainContainer'
import { Tabs } from '../Component/Component'
import AdminFinanceNav from '../../components/AdminFinanceNav'
import Modal from '../../components/UI/Modal'
import {
  fetchDigitalCatalog,
  fetchLicenses,
  fetchPaymentDetails,
  fetchPayments,
  fetchPaymentsOverview,
  fetchTransactions,
  getApiErrorMessage,
  updateLicenseStatus,
} from '../../api/adminPaymentsApi'
import PaymentDetailsModal from './PaymentDetailsModal'
import DigitalOfferModal from './DigitalOfferModal'
import LicenseGrantModal from './LicenseGrantModal'
import '../../scss/components/_adminPaymentsPage.scss'

const TABS = [
  { value: 'overview', text: 'Обзор' },
  { value: 'payments', text: 'Банковские платежи' },
  { value: 'transactions', text: 'Внутренние операции' },
  { value: 'digital', text: 'Цифровые товары' },
  { value: 'licenses', text: 'Лицензии' },
  { value: 'problems', text: 'Проблемы' },
]

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`
const dateTime = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—'
const idOf = (value) => String(value?._id || value?.id || value || '')

const statusLabel = (value) => ({
  CREATED: 'Создан',
  PENDING: 'Ожидает',
  PROCESSING: 'Обрабатывается',
  SUCCEEDED: 'Успешно',
  FAILED: 'Ошибка',
  CANCELED: 'Отменён',
  SUCCESS: 'Успешно',
  ACTIVE: 'Активна',
  REVOKED: 'Отозвана',
  REFUNDED: 'Возврат',
  REVIEWED: 'Проверено',
  UNREVIEWED: 'Не проверено',
}[value] || value || '—')

const purposeLabel = (value) => ({
  BALANCE_TOPUP: 'Пополнение баланса',
  DIGITAL_PURCHASE: 'Цифровая покупка',
  ORDER_PAYMENT: 'Оплата заказа',
}[value] || value || '—')

const paymentMethodLabel = (value) => ({
  CARD: 'Карта',
  USER_BALANCE: 'Личный баланс',
  OFFICE_BALANCE: 'Баланс офиса',
  ADMIN: 'Выдано админом',
  FREE: 'Бесплатно',
}[value] || value || '—')

const transactionTypeLabel = (value) => ({
  TINKOFF_IN: 'T-Bank → баланс',
  QR_IN: 'QR → баланс',
  ADMIN_IN: 'Админ +',
  ADMIN_OUT: 'Админ −',
  TRANSFER_TO_OFFICE: 'Перевод в офис',
  PAYMENT_FOR_SCENE: 'Оплата сцены',
  SUBSCRIPTION_FEE: 'Подписка офиса',
  SCENE_CREATE: 'Создание 3D-сцены',
  SCENE_PACKAGE: 'Пакет 3D-сцен',
  DIGITAL_PURCHASE: 'Цифровая покупка',
}[value] || value || '—')

const entityName = (value) => {
  if (!value) return '—'
  return [value.firstName, value.lastName].filter(Boolean).join(' ')
    || value.name
    || value.email
    || value.username
    || idOf(value)
}

const StatusPill = ({ value }) => (
  <span className={`admin-payments__status admin-payments__status--${String(value || 'unknown').toLowerCase()}`}>
    {statusLabel(value)}
  </span>
)

const Pagination = ({ page, totalPages, pageSize, onPage, onPageSize }) => {
  const safeTotal = Math.max(1, Number(totalPages || 1))
  return (
    <div className="admin-payments__pagination">
      <button type="button" disabled={page <= 0} onClick={() => onPage(page - 1)}>← Назад</button>
      <span>Страница {page + 1} из {safeTotal}</span>
      <button type="button" disabled={page + 1 >= safeTotal} onClick={() => onPage(page + 1)}>Вперёд →</button>
      <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
        {[20, 50, 100, 200].map((size) => <option key={size} value={size}>{size} / стр.</option>)}
      </select>
    </div>
  )
}

const Empty = ({ children = 'Ничего не найдено.' }) => <div className="admin-payments__empty">{children}</div>

const OverviewCard = ({ label, value, sub, tone = '' }) => (
  <article className={`admin-payments__metric ${tone ? `is-${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {sub ? <small>{sub}</small> : null}
  </article>
)

export default function AdminPaymentsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const initialTab = useMemo(() => {
    const requested = new URLSearchParams(location.search).get('tab')
    return TABS.some((tab) => tab.value === requested) ? requested : 'overview'
  }, [location.search])

  const [activeTab, setActiveTab] = useState(initialTab)
  const [overview, setOverview] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [overviewError, setOverviewError] = useState('')

  const [payments, setPayments] = useState([])
  const [paymentsMeta, setPaymentsMeta] = useState({ page: 0, pageSize: 50, totalAll: 0, totalPages: 0 })
  const [paymentFilters, setPaymentFilters] = useState({ search: '', purpose: '', status: '', providerStatus: '', reviewStatus: '' })
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState('')

  const [transactions, setTransactions] = useState([])
  const [transactionsMeta, setTransactionsMeta] = useState({ page: 0, pageSize: 50, totalAll: 0, totalPages: 0 })
  const [transactionFilters, setTransactionFilters] = useState({ search: '', status: '', type: '', targetModel: '' })
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [transactionsError, setTransactionsError] = useState('')

  const [digitalItems, setDigitalItems] = useState([])
  const [digitalMeta, setDigitalMeta] = useState({ page: 0, pageSize: 30, totalAll: 0, totalPages: 0 })
  const [digitalFilters, setDigitalFilters] = useState({ search: '', offerStatus: 'all' })
  const [digitalLoading, setDigitalLoading] = useState(false)
  const [digitalError, setDigitalError] = useState('')

  const [licenses, setLicenses] = useState([])
  const [licensesMeta, setLicensesMeta] = useState({ page: 0, pageSize: 50, totalAll: 0, totalPages: 0 })
  const [licenseFilters, setLicenseFilters] = useState({ search: '', status: '', scopeModel: '', paymentMethod: '' })
  const [licensesLoading, setLicensesLoading] = useState(false)
  const [licensesError, setLicensesError] = useState('')

  const [problemPayments, setProblemPayments] = useState([])
  const [problemMeta, setProblemMeta] = useState({ page: 0, pageSize: 50, totalAll: 0, totalPages: 0 })
  const [problemSearch, setProblemSearch] = useState('')
  const [problemsLoading, setProblemsLoading] = useState(false)
  const [problemsError, setProblemsError] = useState('')

  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailData, setDetailData] = useState(null)
  const [offerModalItem, setOfferModalItem] = useState(null)
  const [grantOffer, setGrantOffer] = useState(null)
  const [licenseActionId, setLicenseActionId] = useState('')
  const [licenseStatusModal, setLicenseStatusModal] = useState(null)
  const [licenseStatusReason, setLicenseStatusReason] = useState('')
  const [licenseStatusError, setLicenseStatusError] = useState('')

  const switchTab = (tab) => {
    setActiveTab(tab)
    navigate(`/admin/payments?tab=${tab}`, { replace: true })
  }

  useEffect(() => {
    if (initialTab !== activeTab) setActiveTab(initialTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab])

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true)
    setOverviewError('')
    try {
      const data = await fetchPaymentsOverview({ rangeDays: 30 })
      setOverview(data)
    } catch (error) {
      setOverviewError(getApiErrorMessage(error, 'Не удалось загрузить обзор'))
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  const loadPayments = useCallback(async (page = paymentsMeta.page, pageSize = paymentsMeta.pageSize, filters = paymentFilters) => {
    setPaymentsLoading(true)
    setPaymentsError('')
    try {
      const data = await fetchPayments({ page, limit: pageSize, ...filters })
      setPayments(data.payments || [])
      setPaymentsMeta({ page: data.page ?? page, pageSize: data.limit ?? pageSize, totalAll: data.totalAll || 0, totalPages: data.totalPages || 0 })
    } catch (error) {
      setPaymentsError(getApiErrorMessage(error, 'Не удалось загрузить платежи'))
    } finally {
      setPaymentsLoading(false)
    }
  }, [paymentFilters, paymentsMeta.page, paymentsMeta.pageSize])

  const loadTransactions = useCallback(async (page = transactionsMeta.page, pageSize = transactionsMeta.pageSize, filters = transactionFilters) => {
    setTransactionsLoading(true)
    setTransactionsError('')
    try {
      const data = await fetchTransactions({ page, limit: pageSize, sort: 'createdAtMinus', ...filters })
      setTransactions(data.transactions || [])
      setTransactionsMeta({ page, pageSize, totalAll: data.totalAll || data.total || 0, totalPages: data.totalPages || 0 })
    } catch (error) {
      setTransactionsError(getApiErrorMessage(error, 'Не удалось загрузить внутренние операции'))
    } finally {
      setTransactionsLoading(false)
    }
  }, [transactionFilters, transactionsMeta.page, transactionsMeta.pageSize])

  const loadDigital = useCallback(async (page = digitalMeta.page, pageSize = digitalMeta.pageSize, filters = digitalFilters) => {
    setDigitalLoading(true)
    setDigitalError('')
    try {
      const data = await fetchDigitalCatalog({ page, limit: pageSize, ...filters })
      setDigitalItems(data.items || [])
      setDigitalMeta({ page: data.page ?? page, pageSize: data.limit ?? pageSize, totalAll: data.totalAll || 0, totalPages: data.totalPages || 0 })
    } catch (error) {
      setDigitalError(getApiErrorMessage(error, 'Не удалось загрузить цифровые товары'))
    } finally {
      setDigitalLoading(false)
    }
  }, [digitalFilters, digitalMeta.page, digitalMeta.pageSize])

  const loadLicenses = useCallback(async (page = licensesMeta.page, pageSize = licensesMeta.pageSize, filters = licenseFilters) => {
    setLicensesLoading(true)
    setLicensesError('')
    try {
      const data = await fetchLicenses({ page, limit: pageSize, ...filters })
      setLicenses(data.licenses || [])
      setLicensesMeta({ page: data.page ?? page, pageSize: data.limit ?? pageSize, totalAll: data.totalAll || 0, totalPages: data.totalPages || 0 })
    } catch (error) {
      setLicensesError(getApiErrorMessage(error, 'Не удалось загрузить лицензии'))
    } finally {
      setLicensesLoading(false)
    }
  }, [licenseFilters, licensesMeta.page, licensesMeta.pageSize])

  const loadProblems = useCallback(async (page = problemMeta.page, pageSize = problemMeta.pageSize, search = problemSearch) => {
    setProblemsLoading(true)
    setProblemsError('')
    try {
      const data = await fetchPayments({ page, limit: pageSize, problemOnly: true, search })
      setProblemPayments(data.payments || [])
      setProblemMeta({ page: data.page ?? page, pageSize: data.limit ?? pageSize, totalAll: data.totalAll || 0, totalPages: data.totalPages || 0 })
    } catch (error) {
      setProblemsError(getApiErrorMessage(error, 'Не удалось загрузить проблемные платежи'))
    } finally {
      setProblemsLoading(false)
    }
  }, [problemMeta.page, problemMeta.pageSize, problemSearch])

  useEffect(() => {
    if (activeTab === 'overview') loadOverview()
    if (activeTab === 'payments') loadPayments(0, paymentsMeta.pageSize)
    if (activeTab === 'transactions') loadTransactions(0, transactionsMeta.pageSize)
    if (activeTab === 'digital') loadDigital(0, digitalMeta.pageSize)
    if (activeTab === 'licenses') loadLicenses(0, licensesMeta.pageSize)
    if (activeTab === 'problems') loadProblems(0, problemMeta.pageSize)
    // deliberate tab-driven fetch; filters apply by explicit button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const refreshCurrent = () => {
    if (activeTab === 'overview') return loadOverview()
    if (activeTab === 'payments') return loadPayments()
    if (activeTab === 'transactions') return loadTransactions()
    if (activeTab === 'digital') return loadDigital()
    if (activeTab === 'licenses') return loadLicenses()
    return loadProblems()
  }

  const openPayment = async (paymentId) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailData(null)
    try {
      setDetailData(await fetchPaymentDetails(paymentId))
    } catch (error) {
      setDetailData({ error: getApiErrorMessage(error, 'Не удалось загрузить платёж') })
    } finally {
      setDetailLoading(false)
    }
  }

  const requestLicenseStatus = (license, nextStatus) => {
    if (!license?._id) return

    setLicenseStatusModal({ license, nextStatus })
    setLicenseStatusReason('')
    setLicenseStatusError('')
  }

  const closeLicenseStatusModal = () => {
    if (licenseActionId) return

    setLicenseStatusModal(null)
    setLicenseStatusReason('')
    setLicenseStatusError('')
  }

  const confirmLicenseStatus = async () => {
    const license = licenseStatusModal?.license
    const nextStatus = licenseStatusModal?.nextStatus

    if (!license?._id || !nextStatus || licenseActionId) return

    const reason = nextStatus === 'REVOKED'
      ? String(licenseStatusReason || '').trim()
      : ''

    if (nextStatus === 'REVOKED' && !reason) {
      setLicenseStatusError('Укажи причину отзыва лицензии.')
      return
    }

    setLicenseActionId(String(license._id))
    setLicenseStatusError('')

    try {
      await updateLicenseStatus(license._id, { status: nextStatus, reason })
      await loadLicenses()
      await loadOverview()
      setLicenseStatusModal(null)
      setLicenseStatusReason('')
    } catch (error) {
      setLicenseStatusError(getApiErrorMessage(error, 'Не удалось изменить лицензию'))
    } finally {
      setLicenseActionId('')
    }
  }

  const renderOverview = () => {
    const p = overview?.payments || {}
    const purposes = overview?.purposes || {}
    const licensesSummary = overview?.licenses || {}
    const offers = overview?.offers || {}
    const tx = overview?.transactions || {}

    return (
      <div className="admin-payments__overview">
        {overviewLoading ? <div className="admin-payments__loading">Собираем финансовый обзор…</div> : null}
        {overviewError ? <div className="admin-payments__error">{overviewError}</div> : null}
        {!overviewLoading && overview ? (
          <>
            <div className="admin-payments__metrics">
              <OverviewCard label="Получено через банк" value={money(p.succeededAmount)} sub={`${p.succeeded || 0} успешных платежей`} tone="success" />
              <OverviewCard label="За последние 30 дней" value={money(p.rangeSucceededAmount)} sub={`${p.rangeSucceeded || 0} успешных из ${p.rangeTotal || 0}`} />
              <OverviewCard label="Ожидают" value={money(p.pendingAmount)} sub={`${p.pending || 0} платежей`} tone="pending" />
              <OverviewCard label="Проблемы" value={p.problems || 0} sub={`не проверено: ${p.unreviewedProblems || 0}`} tone={(p.problems || 0) > 0 ? 'danger' : 'success'} />
            </div>

            <div className="admin-payments__overview-grid">
              <section className="admin-payments__panel">
                <header><h3>За что пришли деньги</h3></header>
                <div className="admin-payments__purpose-list">
                  <button type="button" onClick={() => { setPaymentFilters((v) => ({ ...v, purpose: 'BALANCE_TOPUP' })); switchTab('payments') }}>
                    <span>Пополнение балансов</span><b>{money(purposes.BALANCE_TOPUP?.amount)}</b><small>{purposes.BALANCE_TOPUP?.count || 0} шт.</small>
                  </button>
                  <button type="button" onClick={() => { setPaymentFilters((v) => ({ ...v, purpose: 'DIGITAL_PURCHASE' })); switchTab('payments') }}>
                    <span>Цифровые покупки</span><b>{money(purposes.DIGITAL_PURCHASE?.amount)}</b><small>{purposes.DIGITAL_PURCHASE?.count || 0} шт.</small>
                  </button>
                  <button type="button" onClick={() => { setPaymentFilters((v) => ({ ...v, purpose: 'ORDER_PAYMENT' })); switchTab('payments') }}>
                    <span>Физические заказы</span><b>{money(purposes.ORDER_PAYMENT?.amount)}</b><small>{purposes.ORDER_PAYMENT?.count || 0} шт.</small>
                  </button>
                </div>
              </section>

              <section className="admin-payments__panel">
                <header><h3>Цифровой контур</h3></header>
                <div className="admin-payments__summary-lines">
                  <div><span>Активные предложения</span><b>{offers.active || 0}</b></div>
                  <div><span>Отключённые предложения</span><b>{offers.inactive || 0}</b></div>
                  <div><span>Активные лицензии</span><b>{licensesSummary.ACTIVE || 0}</b></div>
                  <div><span>Отозванные</span><b>{licensesSummary.REVOKED || 0}</b></div>
                  <div><span>Возвращённые</span><b>{licensesSummary.REFUNDED || 0}</b></div>
                </div>
                <div className="admin-payments__panel-actions">
                  <button type="button" onClick={() => switchTab('digital')}>Настроить цифровые товары</button>
                  <button type="button" onClick={() => switchTab('licenses')}>Открыть лицензии</button>
                </div>
              </section>

              <section className="admin-payments__panel">
                <header><h3>Внутренняя экономика</h3></header>
                <div className="admin-payments__summary-lines">
                  <div><span>Успешных проводок</span><b>{tx.total || 0}</b></div>
                  <div><span>Начислено</span><b className="is-positive">{money(tx.positiveAmount)}</b></div>
                  <div><span>Списано</span><b className="is-negative">{money(tx.negativeAmount)}</b></div>
                </div>
                <div className="admin-payments__panel-actions">
                  <button type="button" onClick={() => switchTab('transactions')}>История операций</button>
                  <button type="button" onClick={() => navigate('/admin/billing')}>Промокоды и ручной баланс</button>
                </div>
              </section>

              <section className="admin-payments__panel is-warning">
                <header><h3>Контроль</h3></header>
                <p>FAILED, CANCELED, REFUNDED и ошибки провайдера собраны отдельно. Ручного переключателя «сделать платёж успешным» здесь нет — это защищает деньги от случайной правки.</p>
                <div className="admin-payments__panel-actions">
                  <button type="button" onClick={() => switchTab('problems')}>Открыть проблемы ({p.problems || 0})</button>
                </div>
              </section>
            </div>
          </>
        ) : null}
      </div>
    )
  }

  const renderPaymentFilters = (problems = false) => (
    <div className="admin-payments__filters">
      <input
        placeholder="Поиск: email, имя, PaymentId, OrderId, заказ, описание…"
        value={problems ? problemSearch : paymentFilters.search}
        onChange={(e) => problems ? setProblemSearch(e.target.value) : setPaymentFilters((v) => ({ ...v, search: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') problems ? loadProblems(0) : loadPayments(0)
        }}
      />
      {!problems ? (
        <>
          <select value={paymentFilters.purpose} onChange={(e) => setPaymentFilters((v) => ({ ...v, purpose: e.target.value }))}>
            <option value="">Все назначения</option>
            <option value="BALANCE_TOPUP">Пополнение баланса</option>
            <option value="DIGITAL_PURCHASE">Цифровая покупка</option>
            <option value="ORDER_PAYMENT">Физический заказ</option>
          </select>
          <select value={paymentFilters.status} onChange={(e) => setPaymentFilters((v) => ({ ...v, status: e.target.value }))}>
            <option value="">Все статусы</option>
            <option value="CREATED">CREATED</option>
            <option value="PENDING">PENDING</option>
            <option value="PROCESSING">PROCESSING</option>
            <option value="SUCCEEDED">SUCCEEDED</option>
            <option value="FAILED">FAILED</option>
            <option value="CANCELED">CANCELED</option>
          </select>
          <select value={paymentFilters.reviewStatus} onChange={(e) => setPaymentFilters((v) => ({ ...v, reviewStatus: e.target.value }))}>
            <option value="">Любая проверка</option>
            <option value="UNREVIEWED">Не проверено</option>
            <option value="REVIEWED">Проверено</option>
          </select>
        </>
      ) : null}
      <button type="button" className="admin-payments__primary" onClick={() => problems ? loadProblems(0) : loadPayments(0)}>Применить</button>
      <button type="button" onClick={() => {
        if (problems) {
          setProblemSearch('')
          loadProblems(0, problemMeta.pageSize, '')
        } else {
          const empty = { search: '', purpose: '', status: '', providerStatus: '', reviewStatus: '' }
          setPaymentFilters(empty)
          loadPayments(0, paymentsMeta.pageSize, empty)
        }
      }}>Сбросить</button>
    </div>
  )

  const renderPaymentsTable = (rows, loading, error, problemMode = false) => (
    <>
      {problemMode ? renderPaymentFilters(true) : renderPaymentFilters(false)}
      {loading ? <div className="admin-payments__loading">Загрузка платежей…</div> : null}
      {error ? <div className="admin-payments__error">{error}</div> : null}
      {!loading && !rows.length ? <Empty /> : null}
      {!loading && rows.length ? (
        <div className="admin-payments__table-wrap">
          <table className="admin-payments__table">
            <thead><tr>
              <th>Дата</th><th>Сумма</th><th>Назначение</th><th>Плательщик</th><th>Статус</th><th>T-Bank</th><th>Проверка</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((payment) => (
                <tr key={payment._id} className={payment.status === 'FAILED' || payment.status === 'CANCELED' ? 'is-problem' : ''}>
                  <td>{dateTime(payment.createdAt)}</td>
                  <td><b>{money(payment.amount)}</b>{payment.bonusAmount > 0 ? <small>+{money(payment.bonusAmount)} бонус</small> : null}</td>
                  <td><b>{purposeLabel(payment.purpose)}</b><small>{payment.description || '—'}</small></td>
                  <td><b>{entityName(payment.initiatorId)}</b><small>{payment.initiatorId?.email || idOf(payment.initiatorId)}</small></td>
                  <td><StatusPill value={payment.status} /></td>
                  <td><b>{payment.providerStatus || '—'}</b><small>{payment.providerPaymentId || 'PaymentId ещё нет'}</small></td>
                  <td><StatusPill value={payment.reviewStatus || 'UNREVIEWED'} /></td>
                  <td><button type="button" onClick={() => openPayment(payment._id)}>Подробнее</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  )

  const renderPayments = () => (
    <section className="admin-payments__section">
      <div className="admin-payments__section-head"><div><h2>Банковские платежи</h2><p>Все реальные платежи через T-Bank: пополнения, цифровые покупки и физические заказы.</p></div><b>{paymentsMeta.totalAll} шт.</b></div>
      {renderPaymentsTable(payments, paymentsLoading, paymentsError)}
      <Pagination
        page={paymentsMeta.page}
        totalPages={paymentsMeta.totalPages}
        pageSize={paymentsMeta.pageSize}
        onPage={(page) => loadPayments(page)}
        onPageSize={(pageSize) => loadPayments(0, pageSize)}
      />
    </section>
  )

  const renderTransactions = () => (
    <section className="admin-payments__section">
      <div className="admin-payments__section-head"><div><h2>Внутренние операции</h2><p>Движение личных и офисных балансов. Это не банковская выписка.</p></div><b>{transactionsMeta.totalAll} шт.</b></div>
      <div className="admin-payments__filters">
        <input placeholder="Поиск по пользователю, офису, описанию, ID…" value={transactionFilters.search} onChange={(e) => setTransactionFilters((v) => ({ ...v, search: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && loadTransactions(0)} />
        <select value={transactionFilters.targetModel} onChange={(e) => setTransactionFilters((v) => ({ ...v, targetModel: e.target.value }))}>
          <option value="">User + Office</option><option value="User">User</option><option value="Office">Office</option>
        </select>
        <select value={transactionFilters.type} onChange={(e) => setTransactionFilters((v) => ({ ...v, type: e.target.value }))}>
          <option value="">Все типы</option><option value="TINKOFF_IN">T-Bank пополнение</option><option value="ADMIN_IN">Админ +</option><option value="ADMIN_OUT">Админ −</option><option value="TRANSFER_TO_OFFICE">Перевод в офис</option><option value="DIGITAL_PURCHASE">Цифровая покупка</option><option value="SUBSCRIPTION_FEE">Подписка офиса</option><option value="SCENE_CREATE">Создание сцены</option><option value="SCENE_PACKAGE">Пакет сцен</option><option value="PAYMENT_FOR_SCENE">Legacy-сцена</option>
        </select>
        <select value={transactionFilters.status} onChange={(e) => setTransactionFilters((v) => ({ ...v, status: e.target.value }))}>
          <option value="">Любой статус</option><option value="SUCCESS">SUCCESS</option><option value="PENDING">PENDING</option><option value="FAILED">FAILED</option>
        </select>
        <button type="button" className="admin-payments__primary" onClick={() => loadTransactions(0)}>Применить</button>
      </div>
      {transactionsLoading ? <div className="admin-payments__loading">Загрузка операций…</div> : null}
      {transactionsError ? <div className="admin-payments__error">{transactionsError}</div> : null}
      {!transactionsLoading && !transactions.length ? <Empty /> : null}
      {!transactionsLoading && transactions.length ? (
        <div className="admin-payments__table-wrap"><table className="admin-payments__table"><thead><tr><th>Дата</th><th>Субъект</th><th>Тип</th><th>Сумма</th><th>Баланс</th><th>Связь</th><th>Описание</th></tr></thead><tbody>
          {transactions.map((tx) => <tr key={tx._id}>
            <td>{dateTime(tx.createdAt)}</td>
            <td><b>{tx.targetModel}: {entityName(tx.targetId)}</b><small>{idOf(tx.targetId)}</small></td>
            <td>{transactionTypeLabel(tx.type)}<small>{tx.status}</small></td>
            <td className={Number(tx.amount) >= 0 ? 'is-positive' : 'is-negative'}><b>{Number(tx.amount) > 0 ? '+' : ''}{money(tx.amount)}</b></td>
            <td>{tx.balanceBefore ?? '—'} → {tx.balanceAfter ?? '—'}</td>
            <td><small>{tx.orderId?.orderNumber ? `Заказ ${tx.orderId.orderNumber}` : tx.tinkoffPaymentId ? `PaymentId ${tx.tinkoffPaymentId}` : tx.externalPaymentId ? `Payment ${idOf(tx.externalPaymentId)}` : '—'}</small></td>
            <td>{tx.description || '—'}</td>
          </tr>)}
        </tbody></table></div>
      ) : null}
      <Pagination page={transactionsMeta.page} totalPages={transactionsMeta.totalPages} pageSize={transactionsMeta.pageSize} onPage={(page) => loadTransactions(page)} onPageSize={(size) => loadTransactions(0, size)} />
    </section>
  )

  const renderDigital = () => (
    <section className="admin-payments__section">
      <div className="admin-payments__section-head"><div><h2>Цифровые товары</h2><p>3D-модели с продажей лицензии. Физическая цена изделия здесь не меняется.</p></div><b>{digitalMeta.totalAll} моделей</b></div>
      <div className="admin-payments__filters">
        <input placeholder="Название, артикул, категория, код предложения…" value={digitalFilters.search} onChange={(e) => setDigitalFilters((v) => ({ ...v, search: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && loadDigital(0)} />
        <select value={digitalFilters.offerStatus} onChange={(e) => setDigitalFilters((v) => ({ ...v, offerStatus: e.target.value }))}>
          <option value="all">Все 3D-модели</option><option value="configured">Настроенные</option><option value="unconfigured">Без цифровой продажи</option><option value="active">Продажа активна</option><option value="inactive">Продажа выключена</option>
        </select>
        <button type="button" className="admin-payments__primary" onClick={() => loadDigital(0)}>Применить</button>
      </div>
      {digitalLoading ? <div className="admin-payments__loading">Загрузка 3D-каталога…</div> : null}
      {digitalError ? <div className="admin-payments__error">{digitalError}</div> : null}
      {!digitalLoading && !digitalItems.length ? <Empty>3D-моделей по фильтру нет.</Empty> : null}
      {!digitalLoading && digitalItems.length ? (
        <div className="admin-payments__cards-list">
          {digitalItems.map((item) => {
            const { product, offer } = item
            return <article key={product._id} className="admin-payments__digital-card">
              <div className="admin-payments__digital-main">
                <div className="admin-payments__digital-title"><span>{product.article ? `#${product.article}` : 'Без артикула'}</span><h3>{product.fullName || product.name}</h3><small>{product.categoryName || '—'}</small></div>
                <div className="admin-payments__digital-offer">
                  {offer ? <><StatusPill value={offer.isActive === false ? 'CANCELED' : 'ACTIVE'} /><b>{money(offer.price)}</b><span>{offer.code}</span><small>{[offer.allowCard && 'карта', offer.allowUserBalance && 'личный баланс', offer.allowOfficeBalance && 'офис'].filter(Boolean).join(' · ') || 'оплата отключена'}</small></> : <><span className="admin-payments__status admin-payments__status--unknown">Не настроено</span><b>—</b><small>3D пока работает по legacy-доступу.</small></>}
                </div>
              </div>
              <div className="admin-payments__card-actions">
                <button type="button" className="admin-payments__primary" onClick={() => setOfferModalItem(item)}>{offer ? 'Настроить' : 'Подключить продажу'}</button>
                {offer ? <button type="button" onClick={() => setGrantOffer(offer)}>Выдать лицензию</button> : null}
                <button type="button" onClick={() => navigator.clipboard?.writeText(String(product._id))}>Копировать Product ID</button>
              </div>
            </article>
          })}
        </div>
      ) : null}
      <Pagination page={digitalMeta.page} totalPages={digitalMeta.totalPages} pageSize={digitalMeta.pageSize} onPage={(page) => loadDigital(page)} onPageSize={(size) => loadDigital(0, size)} />
    </section>
  )

  const renderLicenses = () => (
    <section className="admin-payments__section">
      <div className="admin-payments__section-head"><div><h2>Цифровые лицензии</h2><p>Кто и для кого получил доступ. Отзыв лицензии не делает автоматический возврат денег.</p></div><b>{licensesMeta.totalAll} шт.</b></div>
      <div className="admin-payments__filters">
        <input placeholder="Пользователь, офис, товар, offer ID…" value={licenseFilters.search} onChange={(e) => setLicenseFilters((v) => ({ ...v, search: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && loadLicenses(0)} />
        <select value={licenseFilters.status} onChange={(e) => setLicenseFilters((v) => ({ ...v, status: e.target.value }))}><option value="">Все статусы</option><option value="ACTIVE">ACTIVE</option><option value="REVOKED">REVOKED</option><option value="REFUNDED">REFUNDED</option></select>
        <select value={licenseFilters.scopeModel} onChange={(e) => setLicenseFilters((v) => ({ ...v, scopeModel: e.target.value }))}><option value="">USER + OFFICE</option><option value="User">USER</option><option value="Office">OFFICE</option></select>
        <select value={licenseFilters.paymentMethod} onChange={(e) => setLicenseFilters((v) => ({ ...v, paymentMethod: e.target.value }))}><option value="">Любой способ</option><option value="CARD">Карта</option><option value="USER_BALANCE">Личный баланс</option><option value="OFFICE_BALANCE">Офис</option><option value="ADMIN">Админ</option><option value="FREE">Бесплатно</option></select>
        <button type="button" className="admin-payments__primary" onClick={() => loadLicenses(0)}>Применить</button>
      </div>
      {licensesLoading ? <div className="admin-payments__loading">Загрузка лицензий…</div> : null}
      {licensesError ? <div className="admin-payments__error">{licensesError}</div> : null}
      {!licensesLoading && !licenses.length ? <Empty /> : null}
      {!licensesLoading && licenses.length ? (
        <div className="admin-payments__table-wrap"><table className="admin-payments__table"><thead><tr><th>Дата</th><th>Товар / предложение</th><th>Владелец лицензии</th><th>Купил</th><th>Способ</th><th>Статус</th><th></th></tr></thead><tbody>
          {licenses.map((license) => <tr key={license._id}>
            <td>{dateTime(license.createdAt)}</td>
            <td><b>{license.productId?.fullName || license.offerId?.title || '—'}</b><small>{license.offerId?.code || idOf(license.offerId)}</small></td>
            <td><b>{license.scopeModel}: {entityName(license.scopeId)}</b><small>{idOf(license.scopeId)}</small></td>
            <td>{entityName(license.purchasedBy)}<small>{license.purchasedBy?.email || '—'}</small></td>
            <td>{paymentMethodLabel(license.paymentMethod)}<small>{money(license.amount)}</small></td>
            <td><StatusPill value={license.status} />{license.revokeReason ? <small>{license.revokeReason}</small> : null}</td>
            <td>
              {license.status === 'ACTIVE' ? <button type="button" disabled={licenseActionId === String(license._id)} onClick={() => requestLicenseStatus(license, 'REVOKED')}>Отозвать</button> : null}
              {license.status === 'REVOKED' ? <button type="button" disabled={licenseActionId === String(license._id)} onClick={() => requestLicenseStatus(license, 'ACTIVE')}>Восстановить</button> : null}
            </td>
          </tr>)}
        </tbody></table></div>
      ) : null}
      <Pagination page={licensesMeta.page} totalPages={licensesMeta.totalPages} pageSize={licensesMeta.pageSize} onPage={(page) => loadLicenses(page)} onPageSize={(size) => loadLicenses(0, size)} />
    </section>
  )

  const renderProblems = () => (
    <section className="admin-payments__section">
      <div className="admin-payments__section-head"><div><h2>Проблемные платежи</h2><p>FAILED, CANCELED, provider error и возвратные статусы. После проверки можно оставить служебную заметку.</p></div><b>{problemMeta.totalAll} шт.</b></div>
      {renderPaymentsTable(problemPayments, problemsLoading, problemsError, true)}
      <Pagination page={problemMeta.page} totalPages={problemMeta.totalPages} pageSize={problemMeta.pageSize} onPage={(page) => loadProblems(page)} onPageSize={(size) => loadProblems(0, size)} />
    </section>
  )

  const paymentTabs = useMemo(() => (
    TABS.map((tab) => (
      tab.value === 'problems'
        ? {
            ...tab,
            badge: overview?.payments?.unreviewedProblems || 0,
          }
        : tab
    ))
  ), [overview?.payments?.unreviewedProblems])

  const renderTab = () => {
    if (activeTab === 'overview') return renderOverview()
    if (activeTab === 'payments') return renderPayments()
    if (activeTab === 'transactions') return renderTransactions()
    if (activeTab === 'digital') return renderDigital()
    if (activeTab === 'licenses') return renderLicenses()
    return renderProblems()
  }

  return (
    <MainContainer
      title="Платежи и цифровые продажи"
      fluid
      offAll
      buttonText="Обновить"
      handleShow={refreshCurrent}
      headerActions={<AdminFinanceNav />}
      className="admin-payments-shell"
    >
      <div className="admin-payments">
        <Tabs
          items={paymentTabs}
          value={activeTab}
          onChange={switchTab}
          ariaLabel="Разделы платежей"
        />

        {renderTab()}

      <PaymentDetailsModal
        show={detailOpen}
        data={detailData}
        loading={detailLoading}
        onClose={() => { setDetailOpen(false); setDetailData(null) }}
        navigate={navigate}
        onChanged={(updated) => {
          if (!updated) return
          setDetailData((prev) => prev ? { ...prev, payment: { ...prev.payment, ...updated } } : prev)
          setPayments((prev) => prev.map((item) => item._id === updated._id ? { ...item, ...updated } : item))
          setProblemPayments((prev) => prev.map((item) => item._id === updated._id ? { ...item, ...updated } : item))
          loadOverview()
        }}
      />

      <DigitalOfferModal
        show={!!offerModalItem}
        item={offerModalItem}
        onClose={() => setOfferModalItem(null)}
        onSaved={() => {
          setOfferModalItem(null)
          loadDigital()
          loadOverview()
        }}
      />

      <LicenseGrantModal
        show={!!grantOffer}
        offer={grantOffer}
        onClose={() => setGrantOffer(null)}
        onGranted={() => {
          setGrantOffer(null)
          loadLicenses(0)
          loadOverview()
          switchTab('licenses')
        }}
        />

      <Modal
        show={Boolean(licenseStatusModal)}
        handleClose={closeLicenseStatusModal}
        modalTitle={
          licenseStatusModal?.nextStatus === 'REVOKED'
            ? 'Отозвать лицензию'
            : 'Восстановить лицензию'
        }
        size="md"
        onSubmit={confirmLicenseStatus}
        disabledSubmit={
          Boolean(licenseActionId)
          || (
            licenseStatusModal?.nextStatus === 'REVOKED'
            && !String(licenseStatusReason || '').trim()
          )
        }
        confirmText={
          licenseActionId
            ? 'Сохранение…'
            : licenseStatusModal?.nextStatus === 'REVOKED'
              ? 'Отозвать лицензию'
              : 'Восстановить доступ'
        }
        closeText="Отмена"
        preventOutsideClose={Boolean(licenseActionId)}
      >
        <div className="admin-payments__license-status-modal">
          {licenseStatusModal?.nextStatus === 'REVOKED' ? (
            <>
              <div className="admin-payments__info-note">
                Отзыв отключит доступ по лицензии. Деньги автоматически не возвращаются.
              </div>

              <label>
                <span>Причина отзыва</span>
                <textarea
                  rows={4}
                  value={licenseStatusReason}
                  onChange={(event) => setLicenseStatusReason(event.target.value)}
                  placeholder="Например: ошибочная выдача, компенсация отменена, служебная корректировка…"
                  disabled={Boolean(licenseActionId)}
                />
              </label>
            </>
          ) : (
            <div className="admin-payments__info-note">
              Доступ по лицензии будет восстановлен. Финансовые операции при этом не создаются.
            </div>
          )}

          {licenseStatusError ? (
            <div className="admin-payments__error">{licenseStatusError}</div>
          ) : null}
        </div>
      </Modal>
      </div>
    </MainContainer>
  )
}
